import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { EventPublic } from "@dugads/shared";
import { Button } from "@/components/ui/button";
import { Trash2, Clock, ChevronRight, ChevronLeft, Calendar, LayoutTemplate, Send, Ban, Undo2, Check, Loader2, MoreHorizontal, MessageSquare, X } from "lucide-react";
import { PinButton } from "@/components/pin-button";
import { SendMessageButton } from "@/components/send-message-button";
import { SendMessageDialog } from "@/components/send-message-dialog";
import { InlineEditText, InlineEditSelect, InlineEditTime } from "@/components/inline-edit";
import { EventStatusBadge } from "@/components/events/event-status-badge";
import { EventWhereWho } from "@/components/event-detail/event-where-who";
import { fmtTime, fmtDate } from "@/lib/scheduler-utils";
import { useAuth } from "@/lib/auth-context";
import { getListUrl } from "@/lib/use-grid-preferences";
import { useSaveStatus } from "@/lib/save-status";
import { useMediaQuery } from "@/lib/use-media-query";

// 123 R161 -- autosave reassurance pill rendered next to Publish. Shows
// "Saving…" while a PATCH is in flight and "Saved · HH:MM" after it lands.
// Idle (before any edit this session) renders nothing -- no UI noise when
// there's been nothing to save.
function SaveStatusPill() {
  const { phase, lastSavedAt } = useSaveStatus();
  if (phase === "idle") return null;
  if (phase === "saving") {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground" data-status="saving">
        <Loader2 className="h-3 w-3 animate-spin" /> Saving…
      </span>
    );
  }
  const time = lastSavedAt ? new Date(lastSavedAt) : null;
  const label = time
    ? `Saved · ${String(time.getHours()).padStart(2, "0")}:${String(time.getMinutes()).padStart(2, "0")}`
    : "Saved";
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-1 text-xs text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" data-status="saved" title="All edits have been saved">
      <Check className="h-3 w-3" /> {label}
    </span>
  );
}

const SPORT_BG: Record<string, string> = {
  RUG: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
  NET: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300",
  HOC: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
};

const TYPE_BADGE: Record<string, string> = {
  match_day: "rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700",
  practice: "rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700",
  tryout: "rounded bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700",
  tournament: "rounded bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700",
};

const TYPE_OPTIONS = [
  { value: "match_day", label: "Match Day" },
  { value: "practice", label: "Practice" },
  { value: "tryout", label: "Tryout" },
  { value: "tournament", label: "Tournament" },
];

interface EventHeaderProps {
  event: EventPublic;
  canEdit: boolean;
  onUpdate: (field: string, value: unknown) => Promise<void>;
  onDelete: () => void;
  onPublish?: () => void;
  onCancel?: () => void;
  onUnpublish?: () => void;
  // 192 -- provided only when the schedule has matches the caller may manage.
  onSaveAsTemplate?: () => void;
}

export function EventHeader({ event, canEdit, onUpdate, onDelete, onPublish, onCancel, onUnpublish, onSaveAsTemplate }: EventHeaderProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMdUp = useMediaQuery("(min-width: 768px)");
  const eventsUrl = getListUrl(`/schools/${event.school_id}/events`, user?.user.id, "events");
  const sportBadgeClass = event.sport_id ? (SPORT_BG[event.sport_id] ?? "bg-muted text-muted-foreground") : null;
  const hasRoles = (user?.roles.length ?? 0) > 0;

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between" data-region="header">
      <div className="flex flex-col gap-1">
        {/* 198 -- breadcrumb is desktop-only: the mobile shell top bar already
            provides Back, and the wrapping crumb cost 1-2 lines at 390px. */}
        <div className="hidden items-center gap-1.5 text-sm text-muted-foreground md:flex">
          <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center gap-0.5 hover:text-foreground">
            <ChevronLeft className="h-3 w-3" />
            Back
          </button>
          {hasRoles && (
            <>
              <ChevronRight className="h-3 w-3" />
              <Link to={`/schools/${event.school_id}`} className="hover:underline">{event.school_name}</Link>
              <ChevronRight className="h-3 w-3" />
              <Link to={eventsUrl} className="hover:underline">Events</Link>
            </>
          )}
          <ChevronRight className="h-3 w-3" />
          <span className="text-foreground">{event.name}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-semibold">
            <InlineEditText value={event.name} onSave={(v) => onUpdate("name", v)} canEdit={canEdit && event.status !== "cancelled"} inputClassName="h-auto py-0 text-2xl font-semibold" />
          </h1>
          {sportBadgeClass && <span className={`rounded px-2 py-0.5 text-xs font-medium ${sportBadgeClass}`}>{event.sport_name}</span>}
          <InlineEditSelect
            value={event.event_type} displayValue={event.event_type}
            options={TYPE_OPTIONS}
            onSave={(v) => onUpdate("event_type", v)} canEdit={canEdit && event.status === "draft"}
            className={TYPE_BADGE[event.event_type] ?? ""}
          />
          <EventStatusBadge event={event} className="px-2 py-0.5" />
          {/* 134 #145 -- legacy data-field="status" kept on a sibling so existing
              specs/selectors that read the raw status value still resolve. */}
          <span className="sr-only" data-field="status">{event.status}</span>
        </div>
        {event.status === "cancelled" && event.cancel_reason && (
          <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200" data-region="cancel-banner">
            <div className="font-medium">Cancelled{event.cancelled_at ? ` on ${fmtDate(event.cancelled_at)}` : ""}</div>
            <div>{event.cancel_reason}</div>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            <InlineEditTime value={event.starts_at} displayValue={fmtDate(event.starts_at)} onSave={(v) => onUpdate("starts_at", v)} canEdit={canEdit} type="date" />
          </span>
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" />
            <InlineEditTime value={event.starts_at} displayValue={fmtTime(event.starts_at)} onSave={(v) => onUpdate("starts_at", v)} canEdit={canEdit} type="time" compact />
            <span className="text-muted-foreground">-</span>
            <InlineEditTime value={event.ends_at} displayValue={fmtTime(event.ends_at)} onSave={(v) => onUpdate("ends_at", v)} canEdit={canEdit} type="time" compact />
          </span>
        </div>
        {/* 194 -- hosting venue (with directions) + participating schools */}
        <EventWhereWho event={event} className="mt-1" />
      </div>
      {isMdUp ? (
        <div className="flex flex-wrap items-center gap-2">
          <SaveStatusPill />
          <PinButton pinType="event" entityType="Event" entityId={event.id} />
          {event._actions.includes("send_message") && (
            <SendMessageButton contextType="event" contextId={event.id} contextLabel={event.name} variant="outline" showLabel />
          )}
          {onSaveAsTemplate && (
            <Button variant="outline" size="sm" onClick={onSaveAsTemplate} data-action="save-as-template" title="Save this day's layout (fields, times, game lengths) for reuse -- teams are not saved">
              <LayoutTemplate className="mr-2 h-4 w-4" /> Save as template
            </Button>
          )}
          {event._actions.includes("publish") && onPublish && (
            <Button size="sm" onClick={onPublish} data-action="publish">
              <Send className="mr-2 h-4 w-4" /> Publish
            </Button>
          )}
          {event._actions.includes("unpublish") && onUnpublish && (
            <Button variant="outline" size="sm" onClick={onUnpublish} data-action="unpublish" title="Revert to draft (within 1 hour of publishing)">
              <Undo2 className="mr-2 h-4 w-4" /> Unpublish
            </Button>
          )}
          {event._actions.includes("cancel") && onCancel && (
            <Button variant="outline" size="sm" onClick={onCancel} data-action="cancel">
              <Ban className="mr-2 h-4 w-4 text-destructive" /> Cancel Event
            </Button>
          )}
          {event._actions.includes("delete") && (
            <Button variant="outline" size="sm" onClick={onDelete} data-action="delete">
              <Trash2 className="mr-2 h-4 w-4 text-destructive" /> Delete
            </Button>
          )}
        </div>
      ) : (
        <MobileHeaderActions
          event={event}
          onDelete={onDelete}
          onPublish={onPublish}
          onUnpublish={onUnpublish}
          onCancel={onCancel}
          onSaveAsTemplate={onSaveAsTemplate}
        />
      )}
    </div>
  );
}

// 198 -- mobile action row: the five-button pile cost ~3 rows at 390px on
// every tab. Collapse to save-status + the single primary action (Publish,
// the only one with schedule-wide consequence) + a "..." trigger opening a
// 186-pattern bottom sheet with the remaining _actions-gated actions.
// Read-only users (no actions at all) get neither button.
function MobileHeaderActions({
  event,
  onDelete,
  onPublish,
  onUnpublish,
  onCancel,
  onSaveAsTemplate,
}: Omit<EventHeaderProps, "canEdit" | "onUpdate">) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sendMessageOpen, setSendMessageOpen] = useState(false);

  const canPublish = event._actions.includes("publish") && !!onPublish;
  const canUnpublish = event._actions.includes("unpublish") && !!onUnpublish;
  const canCancel = event._actions.includes("cancel") && !!onCancel;
  const canDelete = event._actions.includes("delete");
  const canSendMessage = event._actions.includes("send_message");

  function sheetAction(fn?: () => void) {
    return () => {
      setSheetOpen(false);
      fn?.();
    };
  }

  return (
    <div className="flex flex-wrap items-center gap-2" data-region="mobile-header-actions">
      <SaveStatusPill />
      {canPublish && (
        <Button size="sm" onClick={onPublish} data-action="publish">
          <Send className="mr-2 h-4 w-4" /> Publish
        </Button>
      )}
      {/* Pin is available to any signed-in viewer, so the sheet trigger always renders */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setSheetOpen(true)}
        data-action="open-event-actions"
        aria-label="More actions"
      >
        <MoreHorizontal className="h-4 w-4" />
      </Button>

      {sheetOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-end justify-center bg-black/60"
          data-region="event-actions-sheet"
          onClick={() => setSheetOpen(false)}
        >
          <div
            className="w-full rounded-t-2xl border-t border-border bg-card p-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-2 flex items-center justify-between">
              <p className="text-sm font-semibold">Event actions</p>
              <Button variant="ghost" size="sm" aria-label="Close" onClick={() => setSheetOpen(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-col gap-1">
              {/* PinButton keeps its own toggle/error state; force full-width row styling */}
              <div className="[&_button]:w-full [&_button]:justify-start">
                <PinButton pinType="event" entityType="Event" entityId={event.id} />
              </div>
              {canSendMessage && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start"
                  data-action="send-message"
                  data-context-type="event"
                  data-context-id={event.id}
                  onClick={sheetAction(() => setSendMessageOpen(true))}
                >
                  <MessageSquare className="mr-2 h-4 w-4" /> Send Message
                </Button>
              )}
              {onSaveAsTemplate && (
                <Button variant="outline" size="sm" className="w-full justify-start" onClick={sheetAction(onSaveAsTemplate)} data-action="save-as-template">
                  <LayoutTemplate className="mr-2 h-4 w-4" /> Save as template
                </Button>
              )}
              {canUnpublish && (
                <Button variant="outline" size="sm" className="w-full justify-start" onClick={sheetAction(onUnpublish)} data-action="unpublish">
                  <Undo2 className="mr-2 h-4 w-4" /> Unpublish
                </Button>
              )}
              {canCancel && (
                <Button variant="outline" size="sm" className="w-full justify-start" onClick={sheetAction(onCancel)} data-action="cancel">
                  <Ban className="mr-2 h-4 w-4 text-destructive" /> Cancel Event
                </Button>
              )}
              {canDelete && (
                <Button variant="outline" size="sm" className="w-full justify-start" onClick={sheetAction(onDelete)} data-action="delete">
                  <Trash2 className="mr-2 h-4 w-4 text-destructive" /> Delete
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {sendMessageOpen && (
        <SendMessageDialog
          contextType="event"
          contextId={event.id}
          contextLabel={event.name}
          onClose={() => setSendMessageOpen(false)}
        />
      )}
    </div>
  );
}
